import * as React from 'react';
import { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, Popover, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { MapGalleryPin } from './MapGalleryPin';

export type MapGalleryMarkerPopupPayload = string | { titleHtml: string; bodyHtml: string };

export interface MapGalleryMarkerPopoverHandle {
	loadDetailsIfNeeded: () => void;
	cancelDetailsFetch: () => void;
}

export interface MapGalleryMarkerPopoverProps {
	popup?: MapGalleryMarkerPopupPayload;
	entity?: any;
	popupRenderFunc?: (record: any) => MapGalleryMarkerPopupPayload;
	onFetchDetails?: (entity: any) => Promise<any>;
	getPopupContainer: () => HTMLElement | null | undefined;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
}

function isSplitPayload(
	payload: MapGalleryMarkerPopupPayload
): payload is { titleHtml: string; bodyHtml: string } {
	return (
		typeof payload === 'object' &&
		payload !== null &&
		'titleHtml' in payload &&
		'bodyHtml' in payload
	);
}

function renderPopupPayload(payload: MapGalleryMarkerPopupPayload) {
	if (isSplitPayload(payload)) {
		return (
			<>
				<div
					className="mapbox-gallery__marker-popover-title"
					dangerouslySetInnerHTML={{ __html: payload.titleHtml }}
				/>
				<div
					className="mapbox-gallery__marker-popover-body"
					dangerouslySetInnerHTML={{ __html: payload.bodyHtml }}
				/>
			</>
		);
	}

	return (
		<div
			className="mapbox-gallery__marker-popover-body"
			dangerouslySetInnerHTML={{ __html: payload }}
		/>
	);
}

/**
 * Click-triggered Ant Design Popover for a map pin. Visibility is controlled by
 * the parent (`isOpen` / `onOpenChange`) so only one popover is open at a time.
 * When `onFetchDetails` is provided, the parent starts loading enriched location
 * data via the imperative handle when opening (pin click or sidebar row).
 */
const MapGalleryMarkerPopover = React.forwardRef<
	MapGalleryMarkerPopoverHandle,
	MapGalleryMarkerPopoverProps
>(
	(
		{ popup, entity, popupRenderFunc, onFetchDetails, getPopupContainer, isOpen, onOpenChange },
		ref
	) => {
		const [detailsRecord, setDetailsRecord] = useState<any | null | undefined>(undefined);
		const [loading, setLoading] = useState(false);
		const fetchGenerationRef = useRef(0);
		const fetchInFlightRef = useRef(false);

		const usesAsyncDetails = Boolean(onFetchDetails && entity && popupRenderFunc);

		const cancelDetailsFetch = useCallback(() => {
			fetchGenerationRef.current += 1;
			fetchInFlightRef.current = false;
			setLoading(false);
		}, []);

		const loadDetailsIfNeeded = useCallback(() => {
			if (!usesAsyncDetails || !onFetchDetails || !entity) {
				return;
			}
			if (detailsRecord !== undefined || fetchInFlightRef.current) {
				return;
			}

			fetchInFlightRef.current = true;
			const generation = ++fetchGenerationRef.current;
			setLoading(true);
			onFetchDetails(entity)
				.then((data) => {
					if (generation === fetchGenerationRef.current) {
						setDetailsRecord(data || null);
					}
				})
				.catch(() => {
					if (generation === fetchGenerationRef.current) {
						setDetailsRecord(null);
					}
				})
				.finally(() => {
					fetchInFlightRef.current = false;
					if (generation === fetchGenerationRef.current) {
						setLoading(false);
					}
				});
		}, [usesAsyncDetails, onFetchDetails, entity, detailsRecord]);

		useImperativeHandle(
			ref,
			() => ({
				loadDetailsIfNeeded,
				cancelDetailsFetch,
			}),
			[loadDetailsIfNeeded, cancelDetailsFetch]
		);

		const handleOpenChange = useCallback(
			(open: boolean) => {
				onOpenChange(open);
			},
			[onOpenChange]
		);

		const resolvedPopup = useMemo(() => {
			if (usesAsyncDetails) {
				if (loading || detailsRecord === undefined) {
					return null;
				}
				const recordForPopup = detailsRecord || entity;
				return popupRenderFunc(recordForPopup);
			}
			return popup;
		}, [usesAsyncDetails, loading, detailsRecord, entity, popupRenderFunc, popup]);

		const handleClose = (event: React.MouseEvent) => {
			event.stopPropagation();
			handleOpenChange(false);
		};

		const closeButton = (
			<Button
				type="text"
				size="small"
				className="mapbox-gallery__marker-popover-closeBtn"
				aria-label="Close"
				icon={<CloseOutlined />}
				onClick={handleClose}
			/>
		);

		const textContent = loading ? (
			<div className="mapbox-gallery__marker-popover-loading flex min-h-[72px] items-center justify-center py-4">
				<Spin size="small" />
			</div>
		) : resolvedPopup ? (
			renderPopupPayload(resolvedPopup)
		) : null;

		return (
			<Popover
				visible={isOpen}
				onVisibleChange={handleOpenChange}
				trigger="click"
				placement="top"
				autoAdjustOverflow={true}
				mouseEnterDelay={0}
				mouseLeaveDelay={0}
				overlayClassName="mapbox-gallery__marker-popover"
				getPopupContainer={(triggerNode) => {
					const mapEl = getPopupContainer();
					if (mapEl) {
						return mapEl;
					}
					const fallback = triggerNode.closest('.mapbox-gallery');
					if (fallback) {
						return fallback as HTMLElement;
					}
					return document.body;
				}}
				content={
					<div className="mapbox-gallery__marker-popover-inner">
						<div className="mapbox-gallery__marker-popover-content">{textContent}</div>
						<div className="mapbox-gallery__marker-popover-closeCol">{closeButton}</div>
					</div>
				}
			>
				<span
					className="mapbox-gallery__pin-trigger"
					role="button"
					tabIndex={0}
					onKeyDown={(event) => {
						if (event.key === 'Enter' || event.key === ' ') {
							event.preventDefault();
							handleOpenChange(!isOpen);
						}
					}}
				>
					<MapGalleryPin />
				</span>
			</Popover>
		);
	}
);

export default MapGalleryMarkerPopover;
